import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddShopPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { token, loading: authLoading } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    name_ta: '',
    category_id: '',
    description: '',
    description_ta: '',
    address: '',
    address_ta: '',
    phone: '',
    whatsapp: '',
    email: '',
    website: '',
    latitude: 10.9601,
    longitude: 78.0766,
    images: [],
    working_hours: {}
  });

  useEffect(() => {
    if (authLoading) return;
    
    if (!token) {
      navigate('/login', { state: { from: { pathname: '/add-shop' } } });
      return;
    }
    fetchCategories();
  }, [token, authLoading, navigate]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;

    setUploadingImage(true);
    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`]
        }));
      }
      toast.success(language === 'ta' ? 'படம் பதிவேற்றப்பட்டது' : 'Image uploaded');
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category_id) {
      toast.error(language === 'ta' ? 'வகையைத் தேர்ந்தெடுக்கவும்' : 'Please select a category');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/businesses`, {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      });
      toast.success(language === 'ta' 
        ? 'கடை ஒப்புதலுக்கு சமர்ப்பிக்கப்பட்டது'
        : 'Shop submitted for approval'
      );
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="add-shop-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-8">
        <div className="container mx-auto px-4 md:px-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('home')}
          </Button>
          <h1 className="text-3xl font-bold text-white font-['Manrope']">
            {t('addShopTitle')}
          </h1>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>{t('addShopTitle')}</CardTitle>
            <CardDescription>
              {language === 'ta' 
                ? 'உங்கள் கடை விவரங்களை நிரப்பவும். நிர்வாகி ஒப்புதலுக்குப் பிறகு பட்டியலிடப்படும்.'
                : 'Fill in your shop details. Your listing will be live after admin approval.'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('shopName')} *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="shop-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name_ta">{t('shopNameTa')}</Label>
                  <Input
                    id="name_ta"
                    name="name_ta"
                    value={formData.name_ta}
                    onChange={handleChange}
                    data-testid="shop-name-ta-input"
                  />
                </div>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label>{t('category')} *</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={t('selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {language === 'ta' ? cat.name_ta : cat.name_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="description">{t('description')} *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    data-testid="description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description_ta">{t('descriptionTa')}</Label>
                  <Textarea
                    id="description_ta"
                    name="description_ta"
                    value={formData.description_ta}
                    onChange={handleChange}
                    rows={3}
                    data-testid="description-ta-input"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address">{t('address')} *</Label>
                  <Textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    rows={2}
                    data-testid="address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_ta">{t('addressTa')}</Label>
                  <Textarea
                    id="address_ta"
                    name="address_ta"
                    value={formData.address_ta}
                    onChange={handleChange}
                    rows={2}
                    data-testid="address-ta-input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')} *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="whatsapp">{t('whatsapp')}</Label>
                  <Input
                    id="whatsapp"
                    name="whatsapp"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={handleChange}
                    data-testid="whatsapp-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">{t('website')}</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://"
                    data-testid="website-input"
                  />
                </div>
              </div>

              {/* Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="latitude">{t('latitude')} *</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={handleChange}
                    required
                    data-testid="latitude-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="longitude">{t('longitude')} *</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={handleChange}
                    required
                    data-testid="longitude-input"
                  />
                </div>
              </div>
              <p className="text-sm text-slate-500">
                {language === 'ta' 
                  ? 'குறிப்பு: Google Maps இலிருந்து உங்கள் கடையின் அட்சரேகை மற்றும் தீர்க்கரேகையை பெறலாம்'
                  : 'Tip: You can get coordinates from Google Maps by right-clicking on your shop location'
                }
              </p>

              {/* Images */}
              <div className="space-y-2">
                <Label>{t('uploadImages')}</Label>
                <div className="flex flex-wrap gap-4">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative w-24 h-24">
                      <img
                        src={img}
                        alt={`Upload ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <label className="w-24 h-24 flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                    <Upload className="h-6 w-6 text-slate-400" />
                    <span className="text-xs text-slate-400 mt-1">
                      {uploadingImage ? '...' : 'Upload'}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                      data-testid="image-upload-input"
                    />
                  </label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#E85A24] hover:bg-[#d14918]"
                disabled={loading}
                data-testid="submit-shop-btn"
              >
                {loading ? t('loading') : t('submitForApproval')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddShopPage;
